import React, { useState, useMemo } from 'react';
import { Search, Clock, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BLOG_POSTS } from '../../data/blogPosts';
import { BlogCard } from './BlogCard';
import { LandingNavbar } from '../landing/LandingNavbar';
import { LandingFooter } from '../landing/LandingFooter';
import { fadeInUp, staggerContainer, viewportConfig, scaleIn } from '../../utils/animationVariants';
import { SEO } from '../SEO';

interface BlogHomePageProps {
    onNavigateHome: () => void;
    onNavigateToPost: (slug: string) => void;
    onGetStarted: () => void;
}

export const BlogHomePage: React.FC<BlogHomePageProps> = ({
    onNavigateHome,
    onNavigateToPost,
    onGetStarted
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    const categories = useMemo(() => {
        const allTags = BLOG_POSTS.flatMap(post => post.tags);
        return Array.from(new Set(allTags));
    }, []);

    const filteredPosts = useMemo(() => {
        return BLOG_POSTS.filter(post => {
            const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = selectedCategory ? post.tags.includes(selectedCategory) : true;
            return matchesSearch && matchesCategory;
        });
    }, [searchQuery, selectedCategory]);

    const featuredPost = BLOG_POSTS.find(p => p.featured) || BLOG_POSTS[0];

    return (
        <div className="h-screen overflow-y-auto bg-[#FAFAFA] dark:bg-[#0a0a0f] flex flex-col font-sans selection:bg-indigo-100 dark:selection:bg-indigo-900/30">
            <SEO
                title="NoteSpace Blog | Insights for Visual Thinkers"
                description="Explore articles on visual thinking, AI-powered research, and knowledge management."
                url="https://www.notesscape.in/blog"
            />

            <LandingNavbar
                onGetStarted={onGetStarted}
                onLogoClick={onNavigateHome}
            />

            <main className="flex-1 pt-32 pb-20 px-6">
                <div className="max-w-7xl mx-auto space-y-20">

                    {/* Hero Section */}
                    <div className="text-center max-w-4xl mx-auto space-y-8">
                        <motion.h1
                            initial="hidden"
                            animate="visible"
                            variants={fadeInUp}
                            className="text-5xl md:text-8xl font-black text-slate-900 dark:text-white tracking-tight leading-none"
                        >
                            The <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 dark:from-indigo-400 dark:via-purple-400 dark:to-indigo-400">
                                Collective Mind
                            </span>
                        </motion.h1>
                        <motion.p
                            initial="hidden"
                            animate="visible"
                            variants={fadeInUp}
                            transition={{ delay: 0.1 }}
                            className="text-xl md:text-2xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto"
                        >
                            Exploring the intersection of visual thinking, artificial intelligence, and personal knowledge management.
                        </motion.p>

                        {/* Search & Filter Bar */}
                        <motion.div
                            initial="hidden"
                            animate="visible"
                            variants={fadeInUp}
                            transition={{ delay: 0.2 }}
                            className="relative max-w-2xl mx-auto group"
                        >
                            <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none">
                                <Search className="h-6 w-6 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search our thinking..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-16 pr-8 py-6 bg-white dark:bg-slate-900/50 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-[2rem] shadow-2xl shadow-indigo-500/5 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all text-xl text-slate-900 dark:text-white placeholder:text-slate-400"
                            />
                        </motion.div>

                        {/* Categories */}
                        <motion.div
                            initial="hidden"
                            animate="visible"
                            variants={staggerContainer}
                            className="flex flex-wrap justify-center gap-3"
                        >
                            <motion.button
                                variants={scaleIn}
                                onClick={() => setSelectedCategory(null)}
                                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${selectedCategory === null
                                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xl scale-105'
                                    : 'bg-white dark:bg-slate-900/50 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700'
                                    }`}
                            >
                                All Stories
                            </motion.button>
                            {categories.map(cat => (
                                <motion.button
                                    key={cat}
                                    variants={scaleIn}
                                    onClick={() => setSelectedCategory(cat === selectedCategory ? null : cat)}
                                    className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${selectedCategory === cat
                                        ? 'bg-indigo-600 text-white shadow-xl scale-105'
                                        : 'bg-white dark:bg-slate-900/50 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700'
                                        }`}
                                >
                                    {cat}
                                </motion.button>
                            ))}
                        </motion.div>
                    </div>

                    {/* Featured Post */}
                    {!searchQuery && !selectedCategory && (
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            onClick={() => onNavigateToPost(featuredPost.slug)}
                            className="group cursor-pointer relative rounded-[3rem] overflow-hidden bg-slate-900 h-[600px] shadow-3xl"
                        >
                            <img
                                src={featuredPost.image}
                                alt={featuredPost.title}
                                className="absolute inset-0 w-full h-full object-cover opacity-50 transition-transform duration-1000 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent">
                                <div className="absolute bottom-0 p-12 md:p-20 max-w-5xl">
                                    <div className="flex items-center gap-4 mb-6">
                                        <span className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-black uppercase tracking-[0.2em] rounded-full">
                                            Featured Insight
                                        </span>
                                        <span className="text-slate-300 text-sm font-medium flex items-center gap-2">
                                            <Clock className="w-4 h-4" /> {featuredPost.readTime} read
                                        </span>
                                    </div>
                                    <h2 className="text-4xl md:text-6xl font-black text-white mb-6 leading-[1.1] group-hover:text-indigo-200 transition-colors tracking-tight">
                                        {featuredPost.title}
                                    </h2>
                                    <p className="text-xl text-slate-300 max-w-3xl line-clamp-2 leading-relaxed mb-8">
                                        {featuredPost.excerpt}
                                    </p>
                                    <div className="flex items-center gap-2 text-white font-bold text-lg group-hover:gap-4 transition-all">
                                        Read Story <ArrowRight className="w-6 h-6" />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Post Grid */}
                    <div className="space-y-12">
                        <div className="flex items-center justify-between">
                            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                                {searchQuery || selectedCategory ? 'Found results' : 'Latest Insights'}
                            </h2>
                        </div>

                        {filteredPosts.length > 0 ? (
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={viewportConfig}
                                variants={staggerContainer}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
                            >
                                {filteredPosts.map(post => (
                                    <motion.div key={post.slug} variants={fadeInUp}>
                                        <BlogCard
                                            post={post}
                                            onClick={onNavigateToPost}
                                        />
                                    </motion.div>
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-32 bg-white dark:bg-slate-900/50 backdrop-blur-xl rounded-[2.5rem] border border-dashed border-slate-300 dark:border-slate-800"
                            >
                                <div className="bg-slate-100 dark:bg-slate-800 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8">
                                    <Search className="w-10 h-10 text-slate-400" />
                                </div>
                                <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">No insights found</h3>
                                <p className="text-xl text-slate-500 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                                    We couldn't find any articles matching "{searchQuery}". Try different keywords or browse by category.
                                </p>
                                <button
                                    onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
                                    className="mt-10 px-8 py-4 bg-indigo-600 text-white rounded-full font-bold hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-500/20"
                                >
                                    View all stories
                                </button>
                            </motion.div>
                        )}
                    </div>

                </div>
            </main>

            <LandingFooter />
        </div>
    );
};
